import { type FC, useMemo, useState } from "react";
import Modal from "@via/components/modal.tsx";
import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";
import { CommandLineIcon, KeyIcon } from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers.tsx";
import { getDocsUrl } from "@via/utils/config.tsx";

interface SshKeysModalProps {
  publicKey: string | null;
  setPublicKey: (key: any) => void;
}

const SSH_SETUP_COMMAND_COPY =
  "mkdir -p ~/.ssh && chmod 700 ~/.ssh && touch ~/.ssh/authorized_keys && chmod 600 ~/.ssh/authorized_keys";

const SSH_KEY_MODAL_TABS = [
  { id: "publicKey", label: "Public Key", Icon: KeyIcon },
  { id: "quickInstall", label: "Set Up Remote Access", Icon: CommandLineIcon },
];

type SshKeyModalTabId = (typeof SSH_KEY_MODAL_TABS)[number]["id"];

const SshKeysModal: FC<SshKeysModalProps> = ({ publicKey, setPublicKey }) => {
  const [activeTab, setActiveTab] = useState<SshKeyModalTabId>("publicKey");

  const normalizedPublicKey = useMemo(
    () => (publicKey ?? "").trim(),
    [publicKey]
  );

  const quickInstallCommand = useMemo(() => {
    if (!normalizedPublicKey) return "";
    return `echo '${normalizedPublicKey}' >> ~/.ssh/authorized_keys`;
  }, [normalizedPublicKey]);

  const tabButtonClasses = (isActive: boolean) =>
    classNames(
      "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition",
      isActive
        ? "bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200"
        : "text-gray-600 hover:bg-white/60 hover:text-gray-900"
    );

  return (
    <Modal
      open={!!publicKey}
      setOpen={setPublicKey}
      title="SSH Public Key"
      showButtons={false}>
      <div className="my-5 w-[45rem] max-w-[calc(100vw-2rem)]">
        <div className="mb-4 rounded-lg bg-gray-100 p-1">
          <div className="flex gap-1">
            {SSH_KEY_MODAL_TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={tabButtonClasses(activeTab === id)}>
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "publicKey" ? (
          <>
            <label className="block text-sm mb-2 font-medium leading-6 text-gray-900">
              Public Key
            </label>
            <CopyableText
              value={normalizedPublicKey}
              className="w-full min-w-0 [&>span]:min-w-0 [&>span]:max-w-full"
            />
          </>
        ) : (
          <>
            <label className="block text-sm mb-2 font-medium leading-6 text-gray-900">
              1) Prepare SSH folder (recommended)
            </label>
            <div className="mb-2 text-xs text-gray-500">
              First, connect to your cluster/server via SSH. Then run this to
              create ssh folder and set the correct permissions.
            </div>

            <div className="flex w-full items-center gap-2 max-w-full">
              <textarea
                readOnly
                rows={2}
                value={SSH_SETUP_COMMAND_COPY}
                className="min-w-0 flex-1 w-full bg-gray-100 border border-gray-300 rounded-md text-gray-800 text-sm font-mono px-2 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 whitespace-pre-wrap break-words overflow-hidden leading-normal"
              />
              <CopyableText value={SSH_SETUP_COMMAND_COPY} showValue={false} />
            </div>

            <label className="mt-4 block text-sm mb-2 font-medium leading-6 text-gray-900">
              2) Add your public key
            </label>
            <div className="mb-2 text-xs text-gray-500">
              This appends the public key to{" "}
              <span className="rounded-sm bg-gray-200 px-1 py-0.5 text-gray-800">
                ~/.ssh/authorized_keys
              </span>{" "}
              on your cluster/server.
            </div>

            <div className="flex w-full items-center gap-2 max-w-full">
              <textarea
                readOnly
                rows={6}
                value={quickInstallCommand}
                className="min-w-0 flex-1 w-full bg-gray-100 border border-gray-300 rounded-md text-gray-800 text-sm font-mono px-2 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 whitespace-pre-wrap break-words min-h-40 max-h-60 overflow-auto"
              />
              <CopyableText value={quickInstallCommand} showValue={false} />
            </div>

            <div className="mt-2 text-xs text-gray-500">
              For more details, see{" "}
              <a
                href={getDocsUrl("/user-guides/profile-guide/ssh-credentials/")}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500">
                SSH Credentials documentation
              </a>
              . If you still need help, contact{" "}
              <a
                href="mailto:biocore@umassmed.edu"
                className="font-semibold text-indigo-600 duration-200 ease-in-out hover:text-indigo-500">
                biocore@umassmed.edu
              </a>
              .
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default SshKeysModal;
